import type { Scene } from './types.ts'
import { openingScenes } from './scenes/opening.ts'
import { teamScenes } from './scenes/team.ts'
import { eamScenes } from './scenes/eam.ts'
import { challengeScenes } from './scenes/challenge.ts'
import { solutionScenes } from './scenes/solution.ts'
import { productScenes } from './scenes/products.ts'
import { architectureScenes } from './scenes/architecture.ts'
import { deliveryScenes } from './scenes/delivery.ts'
import { investmentScenes } from './scenes/investment.ts'
import { closingScenes } from './scenes/closing.ts'
import { presentationSections } from './presentation.ts'
import type { PresentationSection } from './presentation.ts'

export * from './types.ts'
export { chapters, chapterById } from './chapters.ts'
export type { PresentationSection } from './presentation.ts'
export { presentationSections } from './presentation.ts'

/**
 * Every scene on the site, in Explore Mode reading order. Keep this in the same order as
 * `chapters`. Presentation Mode does not follow this order — it runs its own sequence,
 * defined in `presentation.ts`, over a subset of these scenes.
 */
export const scenes: Scene[] = [
  ...openingScenes,
  ...teamScenes,
  ...eamScenes,
  ...challengeScenes,
  ...solutionScenes,
  ...productScenes,
  ...architectureScenes,
  ...deliveryScenes,
  ...investmentScenes,
  ...closingScenes,
]

export const sceneByRoute = new Map(scenes.map((s) => [s.route, s]))
export const sceneById = new Map(scenes.map((s) => [s.id, s]))

export function scenesInChapter(chapterId: string): Scene[] {
  return scenes.filter((s) => s.chapter === chapterId)
}

export function chapterSeconds(chapterId: string): number {
  return scenesInChapter(chapterId).reduce((sum, s) => sum + s.estimatedSeconds, 0)
}

export function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = Math.round(totalSeconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/* ============================ PRESENTATION SEQUENCE ============================ */

export interface DeckEntry {
  scene: Scene
  section: PresentationSection
}

/** The recorded presentation, in delivery order. Deck index is the presentation position. */
export const deck: DeckEntry[] = presentationSections.flatMap((section) =>
  section.sceneIds.map((id) => {
    const scene = sceneById.get(id)
    if (!scene) throw new Error(`Presentation section "${section.id}" names unknown scene ${id}`)
    return { scene, section }
  }),
)

export const deckIndexById = new Map(deck.map((entry, i) => [entry.scene.id, i]))

/** Scripted screen time for the whole presentation. */
export const deckTotalSeconds = deck.reduce((sum, e) => sum + e.scene.estimatedSeconds, 0)

/** Cumulative seconds elapsed at the START of each deck position. */
export const deckCumulativeSecondsAt: number[] = deck.reduce<number[]>((acc, _e, i) => {
  acc[i] = i === 0 ? 0 : (acc[i - 1] ?? 0) + (deck[i - 1]?.scene.estimatedSeconds ?? 0)
  return acc
}, [])

/** Sum of the estimates for every deck position from `index` to the end, inclusive. */
export function deckRemainingFrom(index: number): number {
  return deck.slice(index).reduce((sum, e) => sum + e.scene.estimatedSeconds, 0)
}

/**
 * Where Presentation Mode starts when entered from a site route: the scene itself if it is in
 * the deck, otherwise the first deck scene from the same chapter, otherwise the beginning.
 */
export function deckStartForRoute(route: string): number {
  const scene = sceneByRoute.get(route)
  if (!scene) return 0
  const direct = deckIndexById.get(scene.id)
  if (direct !== undefined) return direct
  const sameChapter = deck.findIndex((e) => e.scene.chapter === scene.chapter)
  return sameChapter === -1 ? 0 : sameChapter
}

/**
 * Target delivered length for the recorded proposal video: 60 minutes.
 *
 * The District imposes no duration limit — the RFP and Q&A are silent on it — so this is
 * Merlion's own target, set for the recording. The scene estimates are *screen* time;
 * live narration normally runs longer, which is why the presenter view reports projected
 * finish against this target rather than just elapsed time.
 */
export const TARGET_SECONDS = 60 * 60
